<template>
  <div class="department-container">
    <div class="page-header">
      <h2>部门管理</h2>
      <p>管理公司部门信息</p>
    </div>
    
    <div class="content-area">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>部门列表</span>
            <div class="header-actions">
              <el-button type="primary" size="small" @click="handleAdd">添加部门</el-button>
              <el-button type="danger" size="small" @click="handleBatchDelete" :disabled="selectedDepartments.length === 0">
                批量删除
              </el-button>
              <el-button type="success" size="small" @click="handleImport">导入部门</el-button>
              <el-button type="warning" size="small" @click="handleDownloadTemplate">下载模板</el-button>
              <el-button type="info" size="small" @click="handleRefresh">刷新数据</el-button>
            </div>
          </div>
        </template>
        
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-form :inline="true" :model="searchForm" class="demo-form-inline">
        <el-form-item label="部门名称">
          <el-input v-model="searchForm.department" placeholder="请输入部门名称" clearable />
        </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch">查询</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 部门列表 -->
        <el-table
          :data="departmentList"
          v-loading="loading"
          style="width: 100%"
          @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="department" label="部门名称" width="200" />
          <el-table-column prop="remark" label="部门描述" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status === '1' ? 'success' : 'info'">
                {{ scope.row.status === '1' ? '启用' : '未启用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createtime" label="创建时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.createtime) }}
            </template>
          </el-table-column>
          <el-table-column prop="updatetime" label="更新时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.updatetime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 添加/编辑部门对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      @close="handleDialogClose">
      <el-form
        ref="departmentFormRef"
        :model="departmentForm"
        :rules="departmentRules"
        label-width="80px">
        <el-form-item label="部门名称" prop="department">
          <el-input v-model="departmentForm.department" placeholder="请输入部门名称" />
        </el-form-item>
        <el-form-item label="部门描述">
          <el-input
            v-model="departmentForm.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入部门描述" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="departmentForm.status">
            <el-radio label="1">启用</el-radio>
            <el-radio label="0">未启用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 导入Excel对话框 -->
    <el-dialog
      title="导入部门数据"
      v-model="importDialogVisible"
      width="500px"
      @close="handleImportDialogClose">
      <el-form
        ref="importFormRef"
        :model="importForm"
        :rules="importRules"
        label-width="100px">
        <el-form-item label="Excel文件" prop="file">
          <el-upload
            ref="uploadRef"
            class="upload-demo"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            accept=".xlsx,.xls"
            :file-list="importFile ? [importFile] : []">
            <el-button type="primary">选择文件</el-button>
            <template #tip>
              <div class="el-upload__tip">
                请上传Excel文件，支持 .xlsx 和 .xls 格式
              </div>
            </template>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="handleDownloadTemplate">
            <el-icon><Download /></el-icon>
            下载导入模板
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="importDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleImportSubmit" :loading="importLoading">导入</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Download } from '@element-plus/icons-vue'

export default {
  name: 'DepartmentPage',
  data() {
    return {
      loading: false,
      departmentList: [],
      selectedDepartments: [],
      searchForm: {
        department: ''
      },
      dialogVisible: false,
      dialogTitle: '添加部门',
      submitting: false,
      departmentForm: {
        id: '',
        department: '',
        remark: '',
        status: '1'
      },
      departmentRules: {
        department: [
          { required: true, message: '请输入部门名称', trigger: 'blur' },
          { min: 2, max: 50, message: '部门名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },
      // 导入相关
      importDialogVisible: false,
      importLoading: false,
      importFile: null,
      importForm: {
        file: null
      },
      importRules: {
        file: [
          { required: true, message: '请选择Excel文件', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    departmentFormRef() {
      return this.$refs.departmentFormRef
    }
  },
  mounted() {
    this.getDepartmentList()
  },
  methods: {
    // 获取部门列表
    async getDepartmentList() {
      this.loading = true
      try {
        const response = await this.$axios.get('/api/Department/getlist', {
          params: {
            department: this.searchForm.department
          }
        })
        if (response.code === 1) {
          this.departmentList = response.data || []
        } else {
          ElMessage.error(response.msg || '获取部门列表失败')
        }
      } catch (error) {
        console.error('获取部门列表失败:', error)
        ElMessage.error('获取部门列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.getDepartmentList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        department: ''
      }
      this.getDepartmentList()
    },

    // 添加部门
    handleAdd() {
      this.dialogTitle = '添加部门'
      this.departmentForm = {
        id: '',
        department: '',
        remark: '',
        status: '1'
      }
      this.dialogVisible = true
    },

    // 编辑部门
    handleEdit(row) {
      this.dialogTitle = '编辑部门'
      this.departmentForm = { ...row }
      this.dialogVisible = true
    },

    // 删除部门
    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除部门 "${row.department}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        // 调用删除API，ids用逗号隔开
        const response = await this.$axios.post('/Department/delete', { ids: row.id.toString() })
        if (response.code === 1) {
          ElMessage.success('删除成功')
          this.getDepartmentList()
        } else {
          ElMessage.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除部门失败:', error)
          ElMessage.error('删除失败')
        }
      }
    },

    // 批量删除
    async handleBatchDelete() {
      if (this.selectedDepartments.length === 0) {
        ElMessage.warning('请选择要删除的部门')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${this.selectedDepartments.length} 个部门吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const ids = this.selectedDepartments.map(item => item.id).join(',')
        const response = await this.$axios.post('/Department/delete', { ids })
        if (response.code === 1) {
          ElMessage.success('批量删除成功')
          this.selectedDepartments = []
          this.getDepartmentList()
        } else {
          ElMessage.error(response.msg || '批量删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除部门失败:', error)
          ElMessage.error('批量删除失败')
        }
      }
    },

    // 导入部门
    handleImport() {
      this.importDialogVisible = true
    },

    // 文件选择变化
    handleFileChange(file) {
      this.importFile = file
    },

    // 文件移除
    handleFileRemove() {
      this.importFile = null
    },

    // 导入提交
    async handleImportSubmit() {
      if (!this.importFile) {
        ElMessage.warning('请选择Excel文件')
        return
      }

      this.importLoading = true
      try {
        const formData = new FormData()
        formData.append('file', this.importFile.raw)

        const response = await this.$axios.post('/Department/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.code === 1) {
          ElMessage.success('导入成功')
          this.importDialogVisible = false
          this.getDepartmentList()
        } else {
          ElMessage.error(response.msg || '导入失败')
        }
      } catch (error) {
        console.error('导入失败:', error)
        ElMessage.error('导入失败')
      } finally {
        this.importLoading = false
      }
    },

    // 导入对话框关闭
    handleImportDialogClose() {
      this.importFile = null
      this.$refs.uploadRef?.clearFiles()
    },

    // 下载导入模板
    handleDownloadTemplate() {
      // 创建下载链接指向public目录下的模板文件
      const link = document.createElement('a')
      link.href = '/部门导入模板.xlsx'
      link.setAttribute('download', '部门导入模板.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      ElMessage.success('模板下载成功')
    },


    // 刷新数据
    handleRefresh() {
      this.getDepartmentList()
      ElMessage.success('数据已刷新')
    },

    // 提交表单
    async handleSubmit() {
      if (!this.departmentFormRef) return

      try {
        await this.departmentFormRef.validate()
        this.submitting = true

        let response
        if (this.departmentForm.id) {
          // 编辑部门
          response = await this.$axios.post('/Department/edit', this.departmentForm)
        } else {
          // 添加部门
          response = await this.$axios.post('/Department/add', this.departmentForm)
        }

        if (response.code === 1) {
          ElMessage.success(this.departmentForm.id ? '编辑成功' : '添加成功')
          this.dialogVisible = false
          this.getDepartmentList()
        } else {
          ElMessage.error(response.msg || '操作失败')
        }
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.msg || '操作失败')
        }
      } finally {
        this.submitting = false
      }
    },

    // 对话框关闭
    handleDialogClose() {
      this.departmentFormRef?.resetFields()
    },

    // 选择部门
    handleSelectionChange(selection) {
      this.selectedDepartments = selection
    },

    // 格式化日期
    formatDate(timestamp) {
      if (!timestamp) return '-'
      // 将Unix时间戳转换为毫秒
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.department-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.page-header p {
  margin: 5px 0 0 0;
  color: #909399;
  font-size: 14px;
}

.content-area {
  margin-top: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-area {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
}
</style>
